// @ts-nocheck
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Grid, FormControl, InputLabel, Select, MenuItem, Box, CircularProgress } from '@mui/material';
import StatCard from './StatCard';
import QueriesOverTimeChart from './QueriesOverTimeChart';
import AnswerQualityChart from './AnswerQualityChart';
import FeedbackOverviewCard from './FeedbackOverviewCard';
import TopUsersTable from './TopUsersTable';
import InactiveUsersTable from './InactiveUsersTable';
import { useFetchProjectStats } from '../../hooks/KnowledgeAI/useDashboardApi';

// Defined outside component to keep a stable reference across renders
const emptyFeedback = { good: 0, partiallyCorrect: 0, fullWrong: 0, noFeedback: 0, other: 0 };

interface ProjectWiseOverviewProps {
  onUserClick?: (username: string) => void;
  onFeedbackClick?: (feedbackType: string) => void;
  projects?: any;
  projectsLoading?: boolean;
  // Notifies parent of the currently selected (normalized) project name for feedback context
  onProjectNameChange?: (normalizedProjectName: string) => void;
}

export default function ProjectWiseOverview({
  onUserClick,
  onFeedbackClick,
  projects,
  projectsLoading,
  onProjectNameChange,
}: ProjectWiseOverviewProps) {
  const { data: apiData, loading: projectStatsLoading, fetchProjectStats } = useFetchProjectStats();

  const projectOptions = useMemo(() => {
    if (!projectsLoading && Array.isArray(projects) && projects.length > 0) {
      return projects
        .filter((p: any) => p.ProjectName !== 'CI Heat Data' && p.ProjectName !== 'Claims')
        .map((p: any) => ({ id: String(p.ID), name: p.ProjectName }));
    }
    return [];
  }, [projects, projectsLoading]);

  const [selectedProjectId, setSelectedProjectId] = useState('');

  // Auto-select first project when options load, and fetch its stats
  useEffect(() => {
    if (projectOptions.length > 0) {
      const currentValid = projectOptions.some((p) => p.id === selectedProjectId);
      if (!currentValid) {
        const firstProject = projectOptions[0];
        setSelectedProjectId(firstProject.id);
        const normalizedName = firstProject.name.replace(/&/g, 'and');
        fetchProjectStats(normalizedName);
        onProjectNameChange?.(normalizedName);
      }
    }
  }, [projectOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use API data only — no mock fallback
  const projectDetails = apiData || null;

  const queriesOverTime = projectDetails?.queriesOverTime || [];

  // Normalize answerQuality keys — backend returns "Partially Wrong" (spaced) but components expect camelCase
  const normalizedAnswerQuality = projectDetails?.answerQuality
    ? {
        good: projectDetails.answerQuality.good || 0,
        partiallyCorrect: projectDetails.answerQuality.partiallyCorrect || projectDetails.answerQuality['Partially Wrong'] || 0,
        fullWrong: projectDetails.answerQuality.fullWrong || projectDetails.answerQuality['Fully Wrong'] || 0,
        noFeedback: projectDetails.answerQuality.noFeedback || projectDetails.answerQuality['No Feedback'] || 0,
        other: projectDetails.answerQuality.other || projectDetails.answerQuality['Other'] || 0,
      }
    : emptyFeedback;

  const feedbackCount = normalizedAnswerQuality.good +
    normalizedAnswerQuality.partiallyCorrect +
    normalizedAnswerQuality.fullWrong +
    (normalizedAnswerQuality.other ?? 0);

  const handleProjectChange = useCallback(
    (projectId: string) => {
      setSelectedProjectId(projectId);
      const projectName = projectOptions.find((p) => p.id === projectId)?.name || '';
      const normalizedName = projectName.replace(/&/g, 'and');
      fetchProjectStats(normalizedName);
      onProjectNameChange?.(normalizedName);
    },
    [projectOptions, fetchProjectStats, onProjectNameChange]
  );

  return (
    <>
      {/* Project Selector */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ width: { xs: '100%', sm: 320 } }}>
          <InputLabel>Select Project</InputLabel>
          <Select
            value={selectedProjectId}
            label="Select Project"
            onChange={(e) => handleProjectChange(e.target.value)}
          >
            {projectOptions.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {projectStatsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Summary Stats */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Active Users"
              value={projectDetails?.activeUsers ?? '-'}
              icon="mdi:account-check"
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Queries"
              value={projectDetails?.totalQueries ?? '-'}
              icon="mdi:chat-question"
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Tokens Used"
              value={projectDetails?.tokensUsed ?? '-'}
              icon="mdi:counter"
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Cost"
              value={projectDetails?.totalCost ?? '-'}
              icon="mdi:currency-usd"
              color="error"
              prefix="$"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Documents Uploaded"
              value={projectDetails?.documentsIndexed ?? '-'}
              icon="mdi:file-upload"
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard title="Users Feedback" value={feedbackCount} icon="mdi:message-star" color="secondary" />
          </Grid>

          {/* Queries Over Time — full width */}
          <Grid item xs={12}>
            <QueriesOverTimeChart
              title="Queries Over Time"
              subheader={`${projectDetails?.projectName || ''} query trends`}
              data={queriesOverTime}
            />
          </Grid>

          {/* Answer Quality + Feedback Breakdown — same row */}
          <Grid item xs={12} sm={6}>
            <AnswerQualityChart
              title="Answer Quality"
              subheader="Feedback distribution"
              data={normalizedAnswerQuality}
              onFeedbackClick={onFeedbackClick}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FeedbackOverviewCard
              title="Feedback Breakdown"
              data={normalizedAnswerQuality}
              onFeedbackClick={onFeedbackClick}
            />
          </Grid>

          {/* Top Users Table */}
          <Grid item xs={12}>
            <TopUsersTable
              title="Top Users"
              subheader="Click on a user to view detailed activity"
              users={projectDetails?.topUsers || []}
              onUserClick={onUserClick}
            />
          </Grid>

          {/* Inactive Users Table */}
          <Grid item xs={12}>
            <InactiveUsersTable title="Inactive Users" users={projectDetails?.inactiveUsers || []} />
          </Grid>
        </Grid>
      )}
    </>
  );
}
